import type { UseMutationResult } from "@tanstack/react-query";
import { useRef, useState } from "react";

/**
 * A field's saves, one at a time, while the field stays usable (#241). A
 * control that disabled itself while saving dropped the keyboard's focus to
 * the page, and a second arrow press was lost. Now a change made while a save
 * is in flight is sent once that save is answered: only the latest one, so
 * two saves never race (as Mappings → Autotask settings does, ADR 0082).
 *
 * `saving` is the change not yet answered, for a field to show while it saves:
 * a native select bound to the saved value snapped back, so a second arrow
 * press moved on from the old value (the review of rc.18).
 */
export function useOneSaveAtATime<TData, TError, T>(
  mutation: UseMutationResult<TData, TError, T>,
  /** For one save shared by several fields: changes made meanwhile combine. */
  merge: (queued: T, value: T) => T = (_queued, value) => value,
): { send: (value: T) => void; saving: { value: T } | null } {
  const { mutate } = mutation;
  const next = useRef<{ value: T } | null>(null);
  const inFlight = useRef(false);
  const [saving, setSaving] = useState<{ value: T } | null>(null);

  function run(value: T) {
    inFlight.current = true;
    mutate(value, {
      // Each save's own end, however quickly it came: then the next, if any.
      onSettled: () => {
        inFlight.current = false;
        const queued = next.current;
        if (queued !== null) {
          next.current = null;
          run(queued.value);
        } else {
          setSaving(null);
        }
      },
    });
  }

  function send(value: T) {
    setSaving((shown) => ({ value: shown ? merge(shown.value, value) : value }));
    if (inFlight.current) {
      next.current = { value: next.current ? merge(next.current.value, value) : value };
      return;
    }
    run(value);
  }

  return { send, saving };
}
